import { SearchModel } from './common';

// ─── Master ─────────────────────────────────────────────────────────────────

/**
 * Registro da tabela [ProductApi].[ProductAggregationMaster]
 */
export interface ProductAggregationMaster {
  productAggregationMasterID: number;
  skuMaster: string;
  description: string | null;
  price?: number;
  isActive?: boolean | null;
  isVisibleOnCRM?: boolean | null;
}

export interface ProductAggregationMasterFormData {
  skuMaster: string;
  description?: string;
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/**
 * Registro da tabela [ProductApi].[ProductAggregation]
 * O backend também retorna os campos enriquecidos da tabela de produtos.
 */
export interface ProductAggregationDetail {
  id: number;
  skuMaster: string;
  skuDetail: string;
  skuDetailQty: number;
  isGift: boolean;
  promoPrice: number | null;
  usePromoPrice: boolean | null;
  /** Descrição do produto filho – retornada pela API junto com o detalhe */
  productDescription?: string;
  /** Preço original do produto filho no cadastro de produtos */
  originalPrice?: number;
}

export interface ProductAggregationDetailFormData {
  skuDetail: string;
  skuDetailQty: number;
  isGift: boolean;
  promoPrice?: number | null;
  usePromoPrice?: boolean;
}

export interface ProductAggregationDetailUpdateData {
  skuDetailQty?: number;
  isGift?: boolean;
  promoPrice?: number | null;
  usePromoPrice?: boolean;
}

// ─── Composite (master + details) ────────────────────────────────────────────

/**
 * Produto composto completo (master + lista de detalhes)
 * Retornado pelo endpoint GET /api/product-aggregation/:skuMaster
 */
export interface CompositeProduct extends ProductAggregationMaster {
  details: ProductAggregationDetail[];
  /** Preço total calculado no front (ou pré-calculado pelo backend) */
  totalPrice?: number;
}

// ─── Search ───────────────────────────────────────────────────────────────────

export interface CompositeProductSearchModel extends SearchModel {
  searchSkuMaster?: string;
  searchSkuDetail?: string;
  searchDescription?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Calcula o preço efetivo de um item detalhe levando em conta IsGift e UsePromoPrice.
 * Retorna 0 se IsGift for true, PromoPrice se UsePromoPrice for true, e originalPrice caso contrário.
 */
export function calcDetailEffectivePrice(detail: ProductAggregationDetail): number {
  if (detail.isGift) return 0;
  if (detail.usePromoPrice && detail.promoPrice != null) return detail.promoPrice;
  return detail.originalPrice ?? 0;
}

/**
 * Calcula o preço total de um produto composto somando (qty × preço efetivo) de cada detalhe.
 * Os centavos (parte fracionária) são desconsiderados no total final (Math.floor).
 */
export function calcCompositeTotal(details: ProductAggregationDetail[]): number {
  const raw = details.reduce((sum, d) => sum + d.skuDetailQty * calcDetailEffectivePrice(d), 0);
  return Math.floor(raw);
}
